import React, { useEffect, useState } from 'react';

import { ProgressBar } from '@surface/components';

import {
  TENANT_SWITCH_MIN_OVERLAY_MS,
  useTenantChange,
} from './tenant-change-provider';

/** ease-in cubic: slow start, accelerates toward the end */
const easeInCubic = (ratio: number) => ratio * ratio * ratio;

/**
 * Fixed-duration overlay during tenant switching. Visible for exactly
 * {@link TENANT_SWITCH_MIN_OVERLAY_MS}; progress reaches 100% at hide time.
 */
export const TenantChangeOverlay = () => {
  const { isSwitching, targetTenantName } = useTenantChange();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isSwitching) {
      setProgress(0);
      return;
    }

    const start = performance.now();
    let frameId = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const ratio = Math.min(1, elapsed / (TENANT_SWITCH_MIN_OVERLAY_MS / 2));
      setProgress(easeInCubic(ratio) * 100);

      if (ratio < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameId);
  }, [isSwitching]);

  if (!isSwitching) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm mx-4">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-full">
            <ProgressBar value={progress} className="h-2 w-full" />
          </div>
          <div className="text-center">
            <h3 className="text-md font-semibold text-gray-900">
              Loading {targetTenantName}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              This shouldn't take very long...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
